import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { navSections, legalLinks, trustStrip, type NavLink, type NavSection } from '../data/navigation';
import type { Bi } from '../data/sectors';
import { RelatedLinks } from './RelatedLinks';
import { ContactLinks } from './contact/ContactLinks';
import { hasAnyContact } from '../data/contact';

// Footer columns come from the same navigation source as the header.
const footerItems = (section: NavSection) => section.items.filter(i => i.footer !== false);

// Plain page links start at the top; anchor links are handled by the route-level scroll logic.
const onFooterNav = (to: string) => {
  if (!to.includes('#')) window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'auto'
  });
};
function FooterLink({
  item,
  pick,
  featured
}: {
  item: NavLink;
  pick: (b: Bi) => string;
  featured?: boolean;
}) {
  return <Link to={item.to} onClick={() => onFooterNav(item.to)} className={`font-sans text-sm transition-colors duration-200 hover:text-indigo ${featured ? 'text-black font-medium' : 'text-black/60'}`} data-id="element-3519" data-mp="3l8qwa43iojrg">
      {pick(item.label)}
    </Link>;
}
function MobileNavSection({
  section
}: {
  section: NavSection;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <div className="border-b border-[#E4E6EC] last:border-none" data-id="element-3520" data-mp="34741locg5y7">
      <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="w-full py-4 flex items-center justify-between text-left" data-id="element-3521" data-mp="33nogbm61tnc">
        <span className="font-serif text-lg text-black" data-id="element-3522" data-mp="ct765dy57hgm">{pick(section.label)}</span>
        <ChevronDownIcon className={`w-4 h-4 text-black/40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-3523" data-mp="qthzcnmxd84y" />
      </button>
      <AnimatePresence initial={false} data-id="element-3524" data-mp="msy4jqptqglz">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-3525" data-mp="tvd13wd6udh9">
            <ul className="pb-6 space-y-3" data-id="element-3526" data-mp="13rg2v144oa3o">
              {footerItems(section).map(item => <li key={item.id} data-id="element-3527" data-mp="18ltx30720b6r">
                  <FooterLink item={item} pick={pick} featured={section.id === 'caseStudy'} data-id="element-3528" data-mp="1cmdve73d5kuh" />
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Footer() {
  const {
    lang,
    setLang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <footer className="bg-white pt-16 md:pt-24 pb-8 px-6" data-id="element-3529" data-mp="1rgsfkaynz0aq">
      <div className="max-w-[1400px] mx-auto" data-id="element-3530" data-mp="3ocgjcs8yrj8d">
        <RelatedLinks data-id="element-3531" data-mp="3odhrooylhrme" />

        {/* Desktop & tablet — mirrors the header: Plateforme · Secteurs · Étude de cas · Confiance · Entreprise */}
        <nav className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-10 py-16" aria-label={lang === 'FR' ? 'Plan du site' : 'Site map'} data-id="element-3532" data-mp="2ag5f4qthpcgn">
          {navSections.map(section => <div key={section.id} data-id="element-3533" data-mp="21m0exgsguoog">
              <h3 className="font-serif text-lg text-black mb-6" data-id="element-3534" data-mp="21n1spt0v9p9v">{pick(section.label)}</h3>
              <ul className="space-y-4" data-id="element-3535" data-mp="1wpa4b8zfklaf">
                {footerItems(section).map(item => <li key={item.id} data-id="element-3536" data-mp="2tdg49ileqpid">
                    <FooterLink item={item} pick={pick} featured={section.id === 'caseStudy'} data-id="element-3537" data-mp="2pcebnklzxbpd" />
                  </li>)}
              </ul>
            </div>)}
        </nav>

        {/* Mobile accordion */}
        <nav className="md:hidden py-8" aria-label={lang === 'FR' ? 'Plan du site' : 'Site map'} data-id="element-3538" data-mp="2fezvlm3zioxl">
          {navSections.map(section => <MobileNavSection key={section.id} section={section} data-id="element-3539" data-mp="18w1nxfupohh5" />)}
        </nav>

        {hasAnyContact() && <div className="py-8 border-t border-[#E4E6EC] flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3540" data-mp="2rvr8xjexswhg">
            <p className="font-serif text-lg text-black" data-id="element-3541" data-mp="2wmpouhw0jsk6">{lang === 'FR' ? 'Parlons de votre organisation' : 'Let’s talk about your organization'}</p>
            <ContactLinks location="footer" variant="inline" data-id="element-3542" data-mp="2isygvdxxkgqk" />
          </div>}

        {/* Trust strip */}
        <div className="py-6 border-y border-[#E4E6EC] text-center" data-id="element-3543" data-mp="3bjqkgwhbs7nx">
          <span className="font-sans text-xs tracking-[0.15em] uppercase text-black/50" data-id="element-3544" data-mp="3bki3l7tqkcxb">
            {trustStrip.map((item, i) => <React.Fragment key={item.fr} data-id="element-3545" data-mp="31lj2cabavjkc" data-mpi={i}>
                {i > 0 && <span className="mx-3 text-[#E4E6EC]" data-id="element-3546" data-mp="31l0z2tq8052x">|</span>}
                {pick(item)}
              </React.Fragment>)}
          </span>
        </div>

        {/* Bottom legal bar */}
        <div className="pt-8 flex flex-col lg:flex-row justify-between items-center gap-6 font-sans text-xs text-black/40" data-id="element-3547" data-mp="1q4svqq2yspq7">
          <div data-id="element-3548" data-mp="1v3440wdm2s6b">© 2026 BCITI Inc.</div>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2" data-id="element-3549" data-mp="1nl6a407wxpo7">
            {legalLinks.map((item, i) => <React.Fragment key={item.id} data-id="element-3550" data-mp="1imcthyxbxijt" data-mpi={i}>
                {i > 0 && <span className="text-[#E4E6EC]" data-id="element-3551" data-mp="1wn5sowykt1t9">•</span>}
                <Link to={item.to} onClick={() => onFooterNav(item.to)} className="hover:text-indigo transition-colors duration-200" data-id="element-3552" data-mp="1wkjkqw7rwkai">
                  {pick(item.label)}
                </Link>
              </React.Fragment>)}
          </div>

          <div className="flex items-center gap-2" data-id="element-3553" data-mp="zx6aeasmioxh">
            <button onClick={() => setLang('FR')} aria-pressed={lang === 'FR'} className={lang === 'FR' ? 'text-black font-medium' : 'hover:text-indigo transition-colors duration-200'} data-id="element-3554" data-mp="zuk1a6q341d5">
              FR
            </button>
            <span className="text-[#E4E6EC]" data-id="element-3555" data-mp="l77eyfsgdrlp">|</span>
            <button onClick={() => setLang('EN')} aria-pressed={lang === 'EN'} className={lang === 'EN' ? 'text-black font-medium' : 'hover:text-indigo transition-colors duration-200'} data-id="element-3556" data-mp="l5w641k5htnq">
              EN
            </button>
          </div>
        </div>
      </div>
    </footer>;
}